import { useDispatch, useSelector } from "react-redux";
import type {
  User,
  Organization,
  UserControllerState,
  UserRequestKey,
} from "../types/users";
import type { RootState } from "../store";
import {
  getUsersRequest,
  addUserRequest,
  updateUserRequest,
  deleteUserRequest,
  addOrganizationRequest,
  getUserMeRequest,
  getUserRequest,
  restoreTotpRequest,
  resetPasswordRequest,
  enableUserRequest,
  disableUserRequest,
} from "../store/userControllerDuck";
import { combineErrors, combineStatuses } from "../utils";

export function useUserController() {
  const dispatch = useDispatch();

  const { organizations, roleGroups, users, requests, me }: UserControllerState =
    useSelector((state: RootState) => state.userController);

  const getUserMe = () => {
    dispatch(getUserMeRequest(undefined));
  }

  const loadUsers = () => {
    dispatch(getUserMeRequest(undefined));
    dispatch(getUsersRequest(undefined));
  };

  const getUserInfo = (userId: string) => {
    dispatch(getUserRequest({ userId }));
  };

  const getRequestStatus = (request: UserRequestKey | UserRequestKey[]) =>
    useSelector((state: RootState) => {
      const requests = Array.isArray(request) ? request : [request];
      return combineStatuses(
        requests.map((key) => state.userController.requests[key].status)
      );
    });

  const getRequestError = (request: UserRequestKey | UserRequestKey[]) =>
    useSelector((state: RootState) => {
      const requests = Array.isArray(request) ? request : [request];

      return combineErrors(
        requests.map((key) => state.userController.requests[key].error)
      );
    });

  const restoreTotp = (id: string) => {
    dispatch(restoreTotpRequest({ id }));
  };

  const resetPassword = (id: string, password: string) => {
    dispatch(resetPasswordRequest({ id, password }));
  };

  const enableUser = (id: string) => {
    dispatch(enableUserRequest({ id }));
  };

  const disableUser = (id: string) => {
    dispatch(disableUserRequest({ id }));
  };

  const addUser = (user: User) => {
    dispatch(addUserRequest({ user }));
  };

  const updateUser = (user: User) => {
    dispatch(updateUserRequest({ user }));
  };

  const deleteUser = (id: string) => {
    dispatch(deleteUserRequest({ id }));
  };

  const addOrganization = (organization: Organization) => {
    dispatch(addOrganizationRequest({ organization }));
  };

  return {
    organizations,
    roleGroups,
    users,
    requests,
    me,
    getUserMe,
    getRequestStatus,
    getRequestError,
    loadUsers,
    restoreTotp,
    resetPassword,
    enableUser,
    disableUser,
    getUserInfo,
    addUser,
    updateUser,
    deleteUser,
    addOrganization,
  };
}
